import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReusableTable from './ReusableTable';
import en from '../../copy/en';

export default function NeedsTable({ monthData, currency, onSave }) {
  const [needsData, setNeedsData] = useState(() => {
    // Initialize with real data if available, otherwise empty default
    if (monthData?.needs && monthData.needs.length > 0) {
      return monthData.needs;
    }
    return [{ category: '', amount: 0, id: 'default-needs-row-0' }];
  });
  const isUserOperatingRef = useRef(false);
  const previousMonthDataRef = useRef(null); // Initialize to null, not monthData
  const lastSaveDataRef = useRef(null);

  // Helper function to compare data arrays
  const areDataArraysEqual = useCallback((arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return item1.id === item2.id && 
             item1.category === item2.category && 
             item1.amount === item2.amount;
    });
  }, []);

  // Load data from monthData whenever it changes
  useEffect(() => {
    // Skip if user is actively editing
    if (isUserOperatingRef.current) return;

    // Skip if monthData hasn't changed
    if (monthData === previousMonthDataRef.current) return;

    const hasNeedsData = monthData?.needs && monthData.needs.length > 0;
    const isDifferentFromCurrent = !areDataArraysEqual(monthData?.needs, needsData);

    if (hasNeedsData && isDifferentFromCurrent) {
      // Update with real data from monthData
      setNeedsData(monthData.needs);
    } else if (!hasNeedsData && needsData.length === 0) {
      // No data and no current data - set default row
      setNeedsData([{ category: '', amount: 0, id: 'default-needs-row-0' }]);
    }

    // Update the previous monthData reference
    previousMonthDataRef.current = monthData;
  }, [monthData, needsData, areDataArraysEqual]);

  const handleDataChange = (newData) => {
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;
    
    setNeedsData(newData);
    
    // Calculate total needs
    const totalNeeds = newData.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Save to backend
    if (onSave) {
      onSave({ 
        needs: newData,
        totalNeeds: totalNeeds
      });
    }
    
    // Update the last save data reference
    lastSaveDataRef.current = newData;
    
    // Reset user operating flag after a longer delay to ensure save completes
    setTimeout(() => {
      isUserOperatingRef.current = false;
    }, 1000);
  };

  // Calculate budget goal and determine label and amount
  const calculateBudgetStatus = () => {
    const totalIncome = monthData?.income ? monthData.income.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;

    const needsGoalPercentage = monthData?.budgetGoals?.needsPercentage || 50;
    const needsGoalAmount = totalIncome * (needsGoalPercentage / 100);

    const actualNeedsAmount = needsData.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    if (actualNeedsAmount > needsGoalAmount) {
      return {
        label: "Overspent",
        amount: actualNeedsAmount - needsGoalAmount
      };
    } else if (actualNeedsAmount === needsGoalAmount) {
      return {
        label: "Total",
        amount: actualNeedsAmount
      };
    } else {
      return {
        label: "Left to assign",
        amount: needsGoalAmount - actualNeedsAmount
      };
    }
  };

  const budgetStatus = calculateBudgetStatus();

  const tooltipContent = en.finance.tooltips.needs.description;

  return (
    <ReusableTable
      title="Needs"
      data={needsData}
      onDataChange={handleDataChange}
      addRowLabel="Add need"
      currency={currency}
      showTotal={true}
      totalLabel={budgetStatus.label}
      customTotalAmount={budgetStatus.amount}
      tooltipContent={tooltipContent}
    />
  );
}